/**
 * @file: context.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { ServerUnreachableScreen } from '@euler/app/flows/auth/components/ServerUnreachableScreen';
import {
  isServerUnreachableError,
  isSessionViable,
} from '@euler/app/flows/auth/startupConnectivity';
import { useHandleLoginResult } from '@euler/app/flows/auth/useHandleLoginResult';
import { Center } from '@euler/components/Center';
import { useAppLoading } from '@euler/components/loading/Provider';
import {
  kAgentInfo,
  kAuthProvider,
  kAuthResult,
  kIdentity,
} from '@euler/functions/constants';
import { integrations } from '@euler/lib/integration';
import { sentry } from '@euler/lib/integration/sentry';
import { AuthenticatedUserInfo, UserIdentity, UserProfile } from '@euler/model';
import { AuthProviderType } from '@euler/model/auth';
import { useServiceFactory } from '@euler/services/factory';
import { delayed, makeDebug, onErrorIgnore, onErrorReturn } from '@euler/utils';
import { useBehaviorSubject, usePersistFn } from '@euler/utils/hooks';
import { getInitialURL } from 'expo-linking';
import produce from 'immer';
import React, {
  ReactNode,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ActivityIndicator } from 'react-native';
import { BehaviorSubject, from, switchMap } from 'rxjs';

const debug = makeDebug('auth:context', __DEV__);

export type AuthState =
  | {
      isAuthenticated?: false;
    }
  | {
      isAuthenticated: true;
      authenticatedUserInfo: AuthenticatedUserInfo;
      provider: AuthProviderType | undefined;
    };

export type AuthContextProps = {
  authState$: BehaviorSubject<AuthState>;
};

export const AuthContext = React.createContext<AuthContextProps>(null as any);

export const useAuthContext = () => {
  return useContext(AuthContext);
};

export const useCurrentUser = () => {
  const context = useAuthContext();
  const [state] = useBehaviorSubject(context.authState$);
  if (!state.isAuthenticated) {
    return null;
  }
  return state.authenticatedUserInfo;
};

export const useIsWeixinOpenAuthorized = (props?: {
  onChange?: (value: boolean) => void;
}) => {
  const context = useAuthContext();
  const [state] = useBehaviorSubject(context.authState$);
  const { organizationService, defaultStorageService } = useServiceFactory();
  const isWeixinOpenAuthorized = state.isAuthenticated
    ? Boolean(state.authenticatedUserInfo.org.isWeixinOpenAuthorized)
    : false;

  const updateValue = usePersistFn((value: boolean) => {
    if (!state.isAuthenticated) return;
    if (value !== isWeixinOpenAuthorized) {
      props?.onChange?.(value);
      const updated = produce(state, draft => {
        draft.authenticatedUserInfo.org.isWeixinOpenAuthorized = value;
      });
      void defaultStorageService.set(
        kAuthResult,
        updated.authenticatedUserInfo,
      );
      context.authState$.next(updated);
    }
  });
  useEffect(() => {
    organizationService
      .getIsWeixinOpenAuthorized()
      .then(res => {
        updateValue(res);
      })
      .catch(err => {
        console.error('error get is weixin open authorized status: ', err);
      });
  }, [updateValue, organizationService]);
  return isWeixinOpenAuthorized;
};

export const useCurrentUserDangerously = (): UserProfile => {
  const context = useAuthContext();
  const [state] = useBehaviorSubject(context.authState$);
  if (!state.isAuthenticated) {
    return {
      user: { userId: -1, name: '', nick: '', role: '', userName: '', uid: '' },
      org: { id: -1, name: '' },
      store: { id: -1, name: '' },
    } as any;
  }
  return state.authenticatedUserInfo;
};

export const useCurrentStore = () => {
  return useCurrentUser()?.store;
};

export const useCurrentUserId = () => {
  return useCurrentUser()?.user.userId;
};

export const useCurrentStoreId = () => {
  return useCurrentUser()?.store.id;
};

export const AuthContextProvider = memo(
  ({
    children,
    currentContextKey,
    onServerUnreachable,
  }: {
    children: ReactNode;
    currentContextKey: string;
    // the routes never mount while the server is unreachable, so the host
    // needs this to take down the launch screen.
    onServerUnreachable?: () => void;
  }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [serverUnreachable, setServerUnreachable] = useState(false);
    const [retrying, setRetrying] = useState(false);
    const loading = useAppLoading();
    const authState$ = useMemo(() => new BehaviorSubject<AuthState>({}), []);

    const contextValue = useMemo(
      () => ({ isLoading, authState$ }),
      [authState$, isLoading],
    );

    const [authState] = useBehaviorSubject(authState$);

    const { defaultTokenService, defaultStorageService, authService } =
      useServiceFactory();

    const handleLoginResult = useHandleLoginResult(authState$);

    // used to load the stored auth token and verify it against server
    // if the auth token exists in the storage.
    const loadAndCheckAuthToken = usePersistFn(async () => {
      const [token, identity] = await Promise.all([
        defaultTokenService.getToken().catch(onErrorReturn(null)),
        defaultStorageService
          .get<UserIdentity>(kIdentity)
          .catch(onErrorReturn(null)),
      ]);

      if (!token) {
        setIsLoading(false);
        authState$.next({
          isAuthenticated: false,
        });
        return;
      }

      if (identity) {
        integrations.identifyUser(identity);
      }

      let authProvider: AuthProviderType | null | undefined = undefined;
      try {
        authProvider =
          await defaultStorageService.get<AuthProviderType>(kAuthProvider);
      } catch (e) {
        sentry.captureException(e);
      }

      try {
        const result = await authService.getAuthenticatedUserInfo();
        setServerUnreachable(false);
        if (!result) {
          authState$.next({
            isAuthenticated: false,
          });
        } else {
          authState$.next({
            isAuthenticated: true,
            authenticatedUserInfo: result,
            provider: authProvider ?? undefined,
          });

          if (currentContextKey) {
            delayed(() => {
              loading.show({
                type: 'success',
                message: '已切换',
                duration: 2000,
              });
            }, 1000);
          }
        }
      } catch (e) {
        sentry.captureException(e);

        // the server never ruled on the session: keep the user signed in and
        // let them retry rather than sending them to the login screen.
        if (isServerUnreachableError(e)) {
          const [refreshToken, refreshTokenExpiresAt] = await Promise.all([
            defaultTokenService.getRefreshToken().catch(onErrorReturn(null)),
            defaultTokenService
              .getRefreshTokenExpiry()
              .catch(onErrorReturn(null)),
          ]);
          // the reactive refresh may have rotated the access token.
          const latestToken =
            (await defaultTokenService.getToken().catch(onErrorReturn(null))) ??
            token;
          if (
            isSessionViable({
              isAccessTokenValid: defaultTokenService.isValidToken(latestToken),
              refreshToken,
              refreshTokenExpiresAt,
            })
          ) {
            debug('server unreachable on startup with a viable session: %o', e);
            setServerUnreachable(true);
            onServerUnreachable?.();
            return;
          }
        }

        setServerUnreachable(false);
        // load existing auth result
        try {
          const result =
            await defaultStorageService.get<AuthenticatedUserInfo>(kAuthResult);
          // check the token and redirect to login page if the token has expired.
          if (
            !result?.user ||
            !result.app ||
            !result.platformUserId ||
            !result.platformUid ||
            !result.platformAppId ||
            !defaultTokenService.isValidToken(token)
          ) {
            authState$.next({
              isAuthenticated: false,
            });
          } else {
            authState$.next({
              isAuthenticated: true,
              authenticatedUserInfo: result,
              provider: authProvider ?? undefined,
            });
          }
        } catch (err) {
          sentry.captureException(err);
        }
      } finally {
        setIsLoading(false);
      }
    });

    const retry = usePersistFn(async () => {
      if (retrying) return;
      setRetrying(true);
      try {
        await loadAndCheckAuthToken();
      } catch (e) {
        sentry.captureException(e);
      } finally {
        setRetrying(false);
      }
    });

    useEffect(() => {
      const tryOpenapiSilentLogin = async () => {
        const initialUrl = await getInitialURL();
        const initialSearchParams = new URLSearchParams(
          initialUrl?.split('?')?.[1],
        );
        debug('initial url: %s', initialUrl);

        let success = false;
        const openapiAccessToken = initialSearchParams.get(
          'openapi_access_token',
        );
        const openapiAccessKey = initialSearchParams.get('openapi_access_key');
        if (openapiAccessKey && openapiAccessToken) {
          // do openapi silent login.
          try {
            const result = await authService.login({
              type: AuthProviderType.TokenExchange,
              authToken: openapiAccessToken,
              action: {
                type: 'silent-login',
                extra: openapiAccessKey,
              },
            });
            await handleLoginResult(AuthProviderType.TokenExchange, result);
            success = true;
          } catch (e) {
            sentry.captureException(e);
            alert((e as any).message);
          }
        }
        return success;
      };

      void tryOpenapiSilentLogin().then(success => {
        if (success) {
          setIsLoading(false);
          return;
        }

        loadAndCheckAuthToken().catch(e => {
          sentry.captureException(e);
        });
      });
    }, [authService, handleLoginResult, loadAndCheckAuthToken]);

    const authenticatedUser = authState.isAuthenticated
      ? authState.authenticatedUserInfo
      : ({
          user: {},
          store: {},
          org: {},
        } as unknown as AuthenticatedUserInfo);

    const { userId, uid, userName, nick, name, email, mobile } =
      authenticatedUser.user;

    const { name: storeName } = authenticatedUser.store;
    const { name: orgName, cid } = authenticatedUser.org;

    const identity = useMemo<UserIdentity | null>(
      () =>
        userId && uid && userName
          ? {
              uid,
              userName,
              email: email ? email : `${userName}@${cid}`,
              userIdLong: userId,
              name,
              nick,
              mobile,
              orgName,
              storeName,
            }
          : null,
      [
        userId,
        uid,
        userName,
        email,
        cid,
        name,
        nick,
        mobile,
        orgName,
        storeName,
      ],
    );

    useEffect(() => {
      if (identity) {
        defaultStorageService.set(kIdentity, identity).catch(onErrorIgnore);
        debug('set contextural identity for integrations');
        integrations.identifyUser(identity);
      }
    }, [defaultStorageService, identity]);

    useEffect(() => {
      if (authState.isAuthenticated === false) {
        debug('clear contextural identity for integrations');
        integrations.resetUser();
      }
    }, [authState.isAuthenticated]);

    const saveAuthState = useCallback(
      async (value: AuthState) => {
        try {
          if (value.isAuthenticated) {
            // update cached authenticated user information.
            await defaultStorageService.set(
              kAuthResult,
              value.authenticatedUserInfo,
            );
            await defaultStorageService.set(
              kAgentInfo,
              value.authenticatedUserInfo.agent,
            );
          }
        } catch (e) {
          sentry.captureException(e);
        }
      },
      [defaultStorageService],
    );

    useEffect(() => {
      const sub = authState$
        .pipe(switchMap(value => from(saveAuthState(value))))
        .subscribe();
      return () => sub.unsubscribe();
    }, [authState$, saveAuthState]);

    if (serverUnreachable) {
      return <ServerUnreachableScreen retrying={retrying} onRetry={retry} />;
    }

    if (isLoading) {
      return currentContextKey ? (
        <Center style={{ flex: 1 }}>
          <ActivityIndicator />
        </Center>
      ) : null;
    }

    return (
      <AuthContext.Provider value={contextValue}>
        {children}
      </AuthContext.Provider>
    );
  },
);
